<script lang="ts" setup>
import { computed } from 'vue'
import { usePrevioDS } from '../../composables/helpers'
import { PSpacePrimitive, PSpaceSemantic, PSpaceSize } from './types'

interface Props {
  mobile?: PSpaceSize | PSpaceSemantic | PSpacePrimitive
  laptop?: PSpaceSize | PSpaceSemantic | PSpacePrimitive
  desktop?: PSpaceSize | PSpaceSemantic | PSpacePrimitive
  growMobile?: boolean
  growLaptop?: boolean
  growDesktop?: boolean
  vertical?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  vertical: false,
  growMobile: undefined,
  growLaptop: undefined,
  growDesktop: undefined,
})

const ds = usePrevioDS()

const spaceSize = computed<PSpaceSize | PSpaceSemantic | PSpacePrimitive | undefined>(() => {
  let size = props.mobile

  if (ds.screen().isLaptopAndLarge && props.laptop) {
    size = props.laptop
  }

  if (ds.screen().isDesktop && props.desktop) {
    size = props.desktop
  }

  return size
})

const growClasses = computed<Record<string, boolean>>(() => {
  const mobile = props.growMobile ?? false
  const laptop = props.growLaptop ?? mobile
  const desktop = props.growDesktop ?? laptop

  return {
    'ds-space-grow-mobile': mobile,
    'ds-space-grow-laptop': laptop,
    'ds-space-grow-desktop': desktop,
  }
})
</script>

<template>
  <div
    :class="[
      spaceSize
        ? props.vertical
          ? `ds-space-top-${spaceSize}`
          : `ds-space-start-${spaceSize}`
        : null,
      growClasses,
    ]"
  ></div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/ds.variables.module';

@each $key, $space in $spaces {
  .ds-space-start-#{$key} {
    @include mobile {
      min-width: map-get($space, 'mobile');
    }

    @include laptop {
      min-width: map-get($space, 'laptop');
    }

    @include desktop {
      min-width: map-get($space, 'desktop');
    }
  }

  .ds-space-top-#{$key} {
    @include mobile {
      min-height: map-get($space, 'mobile');
    }

    @include laptop {
      min-height: map-get($space, 'laptop');
    }

    @include desktop {
      min-height: map-get($space, 'desktop');
    }
  }
}

@each $key, $space in $semantic-spaces {
  .ds-space-start-#{$key} {
    @include mobile {
      min-width: var(--space-#{$key}-mobile);
    }

    @include laptop {
      min-width: var(--space-#{$key}-laptop);
    }

    @include desktop {
      min-width: var(--space-#{$key}-desktop);
    }
  }

  .ds-space-top-#{$key} {
    @include mobile {
      min-height: var(--space-#{$key}-mobile);
    }

    @include laptop {
      min-height: var(--space-#{$key}-laptop);
    }

    @include desktop {
      min-height: var(--space-#{$key}-desktop);
    }
  }
}

@each $key, $value in $space-primitives {
  .ds-space-start-#{$key} {
    min-width: var(--#{$key});
  }

  .ds-space-top-#{$key} {
    min-height: var(--#{$key});
  }
}

.ds-space-start-grow {
  flex-grow: 1;
}

.ds-space-top-grow {
  flex-grow: 1;
}

.ds-space-grow-mobile {
  @include mobile {
    flex-grow: 1;
  }
}

.ds-space-grow-laptop {
  @include laptop {
    flex-grow: 1;
  }
}

.ds-space-grow-desktop {
  @include desktop {
    flex-grow: 1;
  }
}
</style>
